/**
 * Query parameters that are explicitly read from URLs on web.
 *
 * By default, URL-provided query params are ignored, as the expo-router wrapper handles passing navigation params internally.
 * (To follow HIPAA best practices, see: crossPlatform/expo-router/index.web.tsx for full details)
 *
 * The query params listed here are used for deeplinks that need it, and are known to not be PHI, and to be HIPAA compliant.
 * Used by the web router wrapper to merge URL-provided query params with the app-internal sessionStorage params.
 */
const AllowedUrlProvidedNavParamOverrides = new Set<string>([
  // sub-deep-link for /schedule?target=appointment
  "appointmentExternalId",
  // sub-deep-link for /messages: the channel prefix identifies the channel to open (not PHI/PII)
  "channel",
  // deep-link for /video-content specifies analytics source
  "componentSource",
  // sub-deep-link for /journal opens a specific SMT journal entry (not PHI/PII)
  "entry-id",
  // sub-deep-link for /explore?target=search-groups opens a specific group class (not PHI/PII)
  "group-id",
  // sub-deep-link for /schedule?target=group-class-event
  "groupClassEventExternalId",
  // sub-deep-link for /messages: support-staff user id disambiguating a SUP_ channel (not PHI/PII)
  "id",
  // sub-deep-link specifying target on a given page (eg, "book-appointment", "search-groups")
  "target",
  // sub-deep-link for /home?target=task opens a specific task (not PHI/PII)
  "task-id",
  // deep-link for /video-content specifies the title for the video-content modal
  "title",
  // deep-link for /video-content specifies the URL for the video
  // the modal itself restricts this equip's s3 bucket for videos (and on web, the Content-Security-Policy does as well)
  "url",
]);

const RouteParamsStorageKey = "hipaa-safe-route-params";

// Filters user-provided URL query string params to only those in an allowlist, to prevent user from arbitrary nav param manipulation
const filterToAllowedUrlParams = (search: string): Record<string, string> =>
  Object.fromEntries(
    [...new URLSearchParams(search).entries()].filter(([key]) =>
      AllowedUrlProvidedNavParamOverrides.has(key),
    ),
  );

export { filterToAllowedUrlParams, RouteParamsStorageKey };
